<template>
  <div class="routeInfo">
    <div class="routeInfo--block flex flex-column">
      <span class="routeInfo--block--address" v-text="terminal.name" />
    </div>
    <div class="routeInfo--row">
      <div class="routeInfo--block">
        <span class="routeInfo--block--header">Контакты</span>
        <p class="routeInfo--block--description" v-text="terminal.address" />
        <p v-if="terminal.note" class="routeInfo--block--description" v-text="terminal.note" />
      </div>
      <div class="routeInfo--block flex flex-column">
        <span class="routeInfo--block--header">График работы</span>
        <div v-if="days" class="routeInfo--block--day--wrapper">
          <div
            v-for="day in days"
            :key="day.name"
            :class="day.class"
          >
            <span class="routeInfo--block--day--name" :class="{'routeInfo--block--day--holiday': !day.timetable.isWorking}" v-text="day.name" />
            <div v-if="day.timetable.isWorking">
              <span
                class="routeInfo--block--day--time"
                v-text="day.timetable.from === '00:01' ? '24' : day.timetable.from"
              />
              <span
                class="routeInfo--block--day--time"
                v-text="day.timetable.to === '23:59' ? 'часа' : day.timetable.to"
              />
            </div>
            <div v-if="!day.timetable.isWorking" class="m-auto">
              <span> - </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="terminal.description" class="routeInfo--block routeInfo--block--restrictions flex flex-column">
      <span class="routeInfo--block--header">Ограничения по грузу</span>
      <span class="routeInfo--block--description" v-text="terminal.description" />
    </div>
    <div class="routeInfo--buttons">
      <vz-button
        variant="primary"
        :to="{ name: 'index' }"
        target="_blank"
        large
        outline
      >
        <span class="whitespace-nowrap">На главную</span>
      </vz-button>
      <vz-button
        variant="primary"
        target="_blank"
        large
        outline
        :to="contactRoute"
      >
        Контакты
      </vz-button>
      <vz-button
        variant="primary"
        large
        @click="print"
      >
        Печать
      </vz-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import VzButton from '~/components/ui-kit/vz-button.vue';
import {PropType} from 'vue';
import {TerminalDetail, TerminalTimeTable} from '~/types/types';
import {RouteLocationRaw} from 'vue-router';

const props = defineProps({
  terminal: {
    type: Object as PropType<TerminalDetail>,
    default: () => ({})
  }
})

const daysConst = [
  {
    name: 'ПН',
    dayCount: 1,
    property: 'workday'
  },
  {
    name: 'ВТ',
    dayCount: 2,
    property: 'workday'
  },
  {
    name: 'СР',
    dayCount: 3,
    property: 'workday'
  },
  {
    name: 'ЧТ',
    dayCount: 4,
    property: 'workday'
  },
  {
    name: 'ПТ',
    dayCount: 5,
    property: 'workday'
  },
  {
    name: 'СБ',
    dayCount: 6,
    property: 'saturday'
  },
  {
    name: 'ВС',
    dayCount: 0,
    property: 'sunday'
  }
]

const days = computed((): {name: string, timetable: TerminalTimeTable[0], isCurrent: boolean, class: string}[] | false => {
  if (props.terminal?.timetable) {
    return daysConst.map(day => ({
      name: day.name,
      class: getClassForDay(day.dayCount),
      timetable: props.terminal?.timetable?.[day.property],
      isCurrent: isCurrentDay(day.dayCount)
    }))
  } else {
    return false
  }
})

const contactRoute = computed((): RouteLocationRaw => {
  return { name: 'contacts', params: { tag: 'contacts' }, query: { terminal: props.terminal.address.trim() } }
})

function getClassForDay (day: number): string {
  const dayClass = 'flex flex-column flex-align-items-center routeInfo--block--day'
  return `${dayClass} ${isCurrentDay(day) ? 'active' : ''}`
}

function isCurrentDay (day: number): boolean {
  const dateCurrent = new Date()
  return dateCurrent.getDay() === day
}

function print (): void {
  window.print()
}
</script>

<style lang="scss">
.routeInfo {
  padding: 20px 0;

  &--row {
    display: flex;
    justify-content: space-between;

    @media screen and (max-width: $lg) {
      flex-direction: column;
    }

    .routeInfo--block:first-child {
      margin-right: 20px;

      @media screen and (max-width: $lg) {
        margin-right: 0;
      }
    }
  }

  &--border {
    border-bottom: 1px solid $font-medium;
  }

  &--block {
    padding-bottom: 16px;
    font-size: $fs-16;

    &--header {
      font-size: $fs-16;
      font-weight: 700;
    }

    &--address {
      font-weight: 700;
      font-size: 18px;
      line-height: 20px;
    }

    &--description {
      color: $font-low;
      margin-top: 10px;
      font-size: $fs-14;
      font-weight: 400;
    }

    &--day {
      width: 60px;
      padding: 10px;

      @media screen and (max-width: $sm) {
        font-size: 14px;
        width: auto;
        padding: 5px 3px;
      }

      &--wrapper {
        display: flex;
        margin-top: 10px;
        font-size: $fs-14;
      }

      &.active {
        background-color: rgba(74, 73, 73, 0.2);
      }

      &--name {
        padding-bottom: 8px;
        margin-bottom: 8px;
        text-align: center;
        width: 100%;
        border-bottom: 2px solid $light-green;
      }

      &--holiday {
        border-color: $primary;
      }

      &--time {
        width: 100%;
        display: block;
        text-align: center;
      }
    }
  }

  &--buttons {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .vz-button {
      display: flex;
      justify-content: center;
      max-width: 200px;
      width: 100%;

      @media screen and (max-width: $lg) {
        max-width: 150px;
      }

      @media screen and (max-width: $sm) {
        max-width: none;
        margin: 10px 0;
      }
    }

    @media screen and (max-width: $sm) {
      flex-direction: column;
      align-items: center;
    }
  }

  @media print {
    .routeInfo--block--restrictions,
    .routeInfo--buttons {
      display: none !important;
    }
  }
}
</style>
